import ms from 'ms';
import pRetry from 'p-retry';
import * as os from 'node:os';
import * as portfinder from 'portfinder';
import * as childProcess from 'node:child_process';
import * as utils from '../utils';
import * as httpRequest from '../commons/httpRequest';
import * as servicesApi from '../commons/testimServicesApi';
import * as fs from 'node:fs';
import * as fsPromises from 'node:fs/promises';
import { join } from 'node:path';
import { ArgError } from '../errors';
import { getLogger } from '../commons/logger';
import { getExtensionsUrl } from '../runOptionsUtils';
import { gridTypes, CLI_MODE } from '../commons/constants';
import { featureFlagsService } from '../commons/featureFlagsService';
import { spreadObjectToArgs } from '../utils';
import { getRegionFromProjectId } from '../utils/regionUtils';
import type { OSS } from '../utils';
import type { RunnerMode } from '../runners/runnerUtils';
import type { SerializedGrid } from './gridService';
import type { RunnerOptions, TunnelOptions } from '../runOptions';
import type { LambdaTestIntegrationData } from 'clickim/src/common/api/testimVisualTestingApi';
const logger = getLogger('lambdatestService');

const LT_TUNNEL_BINARY_ORIGIN = 'https://downloads.lambdatest.com/tunnel/v3';
const LT_TUNNEL_BINARY_PATHNAME = {
    win32ia32: 'windows/32bit/LT_Windows.zip',
    win32x64: 'windows/64bit/LT_Windows.zip',
    darwinia32: 'mac/32bit/LT_Mac.zip',
    darwinx64: 'mac/64bit/LT_Mac.zip',
    darwinarm64: 'mac/64bit/LT_Mac.zip',
    linuxia32: 'linux/32bit/LT_Linux.zip',
    linuxx64: 'linux/64bit/LT_Linux.zip',
    freebsdia32: 'freebsd/32bit/LT_Freebsd.zip',
    freebsdx64: 'freebsd/64bit/LT_Freebsd.zip',
};
const LT_TUNNEL_BINARY_DIRECTORY = `${os.tmpdir()}/LT`;
const LT_TUNNEL_BINARY_LOCATION = `${LT_TUNNEL_BINARY_DIRECTORY}/LT`;

const LT_MINIMUM_CONNECTION_RETRY_TIMEOUT = ms('15m');

type LambdaTestConfig = utils.AwaitedReturnType<typeof servicesApi.fetchLambdatestConfig>;

const MAX_RESOLUTION = '2560x1440';
export class LambdatestService {
    isActive = false;
    static tunnel: childProcess.ChildProcess | undefined;
    static tunnelName: string | undefined;
    static lambdatestConfigPromise: Promise<LambdaTestConfig> | undefined;
    static lambdatestConfig: LambdaTestConfig | undefined;
    public smartUIIntegrationData?: Partial<LambdaTestIntegrationData> | null;

    public static isLambdatestGrid(gridData: SerializedGrid) {
        return (
            gridData.type === gridTypes.TESTIM_LAMBDATEST ||
            gridData.type === gridTypes.LAMBDATEST ||
            (gridData.type === gridTypes.HYBRID && gridData.provider === 'lambdatest')
        );
    }

    public isLambdatestRun() {
        return this.isActive;
    }

    public async enableIfNeeded(gridData: SerializedGrid, companyId: string, projectId?: string) {
        if (!LambdatestService.isLambdatestGrid(gridData)) {
            return;
        }

        LambdatestService.lambdatestConfigPromise ||= servicesApi.fetchLambdatestConfig(companyId, projectId);
        LambdatestService.lambdatestConfig = await LambdatestService.lambdatestConfigPromise;
        this.isActive = true;
        
        if (projectId) {
            try {
                const testimServicesApi = await import('../commons/testimServicesApi');
                this.smartUIIntegrationData = await testimServicesApi.getVisualTestingIntegrationData(projectId, 'lambdatest');
            } catch {
                // SmartUI integration data is optional — don't block grid usage on a 404.
            }
        }
    }

    public disable() {
        this.isActive = false;
    }

    public get getSessionTimeout() {
        if (!this.isActive) {
            return null;
        }
        return LT_MINIMUM_CONNECTION_RETRY_TIMEOUT;
    }

    public get getSessionRetries() {
        if (!this.isActive) {
            return null;
        }
        return 1;
    }

    // https://www.lambdatest.com/support/docs/beta-lambda-tunnel-for-corporate-firewalls/
    private static async prepareTunnel() {
        const isBinaryExist = await utils.doesPathExist(LT_TUNNEL_BINARY_LOCATION);
        if (isBinaryExist) {
            return;
        }

        const downloadUrl =
            LT_TUNNEL_BINARY_PATHNAME[(os.platform() + os.arch()) as keyof typeof LT_TUNNEL_BINARY_PATHNAME];
        if (!downloadUrl) {
            throw new Error(`tunnel on ${os.platform() + os.arch()} platform is not supported.`);
        }
        const zipLocation = `${LT_TUNNEL_BINARY_DIRECTORY}.zip`;
        await utils.downloadAndSave(`${LT_TUNNEL_BINARY_ORIGIN}/${downloadUrl}`, zipLocation);
        await utils.unzipFile(zipLocation, LT_TUNNEL_BINARY_DIRECTORY);
    }

    private static async chmodLtTunnel(path: string): Promise<void> {
        if (os.platform() === 'win32') {
            return;
        }

        const fileExists = await utils.doesPathExist(path);

        if (!fileExists) {
            return;
        }

        try {
            await fsPromises.access(path, fs.constants.X_OK);
            return;
        } catch {
            // do nothing.
        }

        try {
            await fsPromises.chmod(path, '777');
        } catch (e) {
            logger.error('Failed to make LT tunnel executable', { e });
            throw e;
        }
    }

    // keeping this static and public for easy mock in tests.
    public static async delay(time: number) {
        await utils.delay(time);
    }

    public static async connectTunnel(runnerOptions: Partial<TunnelOptions> | RunnerOptions) {
        if ((runnerOptions as RunnerOptions).externalLambdatestTunnelId) {
            LambdatestService.tunnelName = (runnerOptions as RunnerOptions).externalLambdatestTunnelId;
            return;
        }
        await this.prepareTunnel();
        const infoAPIPort = await portfinder.getPortPromise();
        const {
            gridData = {},
            gridUsername,
            gridPassword,
            externalLambdatestNTLMTunnelUsername,
            externalLambdatestNTLMTunnelPassword,
        } = runnerOptions as RunnerOptions;
        const proxyUri = global.proxyUri;
        LambdatestService.tunnelName = utils.guid();

        let tunnelArgs = ['--tunnelName', LambdatestService.tunnelName, '--infoAPIPort', String(infoAPIPort)];

        if (featureFlagsService.flags.enableLambdaTestTunnelNTLM.isEnabled()) {
            tunnelArgs = [...tunnelArgs, '--ntlm'];

            // CLI flags take precedence over feature flags
            if (externalLambdatestNTLMTunnelUsername && externalLambdatestNTLMTunnelPassword) {
                tunnelArgs = [
                    ...tunnelArgs,
                    '--ntlm-username',
                    externalLambdatestNTLMTunnelUsername,
                    '--ntlm-password',
                    externalLambdatestNTLMTunnelPassword,
                ];
            } else {
                const customTunnelOptions = JSON.parse(
                    featureFlagsService.flags.addCustomLTtunnelNTLMOptions.getValue(),
                );
                if (customTunnelOptions) {
                    tunnelArgs = [...tunnelArgs, ...spreadObjectToArgs(customTunnelOptions)];
                }
            }
        }

        if (featureFlagsService.flags.enableLambdaTestTunnelSkipUpgrade.isEnabled()) {
            tunnelArgs = [...tunnelArgs, '--skip-upgrade'];
        }

        if ((runnerOptions as RunnerOptions).externalLambdatestUseWss) {
            tunnelArgs = [...tunnelArgs, '--mode', 'ws'];
        }
        if ((runnerOptions as RunnerOptions).externalLambdatestDisableAutomationTunneling) {
            tunnelArgs = [
                ...tunnelArgs,
                '--bypassHosts',
                'run.testim.io,services.testim.io,*.betterstackdata.com,conf.rollout.io,statestore.rollout.io,push.rollout.io,analytic.rollout.io',
            ];
        }

        if (gridData.tunnelUser && gridData.tunnelKey) {
            tunnelArgs = [...tunnelArgs, '--user', gridData.tunnelUser, '--key', gridData.tunnelKey];
        } else if (gridUsername && gridPassword) {
            tunnelArgs = [...tunnelArgs, '--user', gridUsername, '--key', gridPassword];
        } else {
            throw new ArgError('tunnel requires username and password');
        }

        if (proxyUri) {
            try {
                const proxyUrl = new URL(proxyUri);
                tunnelArgs = [...tunnelArgs, '--proxy-host', proxyUrl.hostname];
                if (proxyUrl.port) {
                    tunnelArgs = [...tunnelArgs, '--proxy-port', proxyUrl.port];
                }
                if (proxyUrl.username && proxyUrl.password) {
                    tunnelArgs = [...tunnelArgs, '--proxy-user', proxyUrl.username, '--proxy-pass', proxyUrl.password];
                }
            } catch {
                throw new ArgError('proxy url is invalid');
            }
        }

        if ((runnerOptions as RunnerOptions).externalLambdatestMitm) {
            tunnelArgs = [...tunnelArgs, '--mitm'];
        }

        // It might take some time until the file is ready.
        await LambdatestService.delay(3000);
        await LambdatestService.chmodLtTunnel(join(LT_TUNNEL_BINARY_DIRECTORY, 'LT'));
        LambdatestService.tunnel = childProcess.spawn('./LT', tunnelArgs, { cwd: LT_TUNNEL_BINARY_DIRECTORY });

        let stdoutResult = '';
        let stderrResult = '';

        LambdatestService.tunnel.stdout!.on('data', (data) => {
            stdoutResult += data.toString();
        });

        LambdatestService.tunnel.stderr!.on('data', (data) => {
            stderrResult += data.toString();
        });

        // verify that LT tunnel strated successfully
        try {
            const ltInfo = await pRetry(
                () =>
                    httpRequest.get(`http://127.0.0.1:${infoAPIPort}/api/v1.0/info`, {}, {}, undefined, {
                        skipProxy: true,
                    }),
                {
                    onFailedAttempt: (error) => {
                        if (stdoutResult.includes('Invalid Credentials')) {
                            throw Object.assign(error, { isInvalidCredsForLT: true });
                        }
                    },
                    retries: 30,
                    minTimeout: 2000,
                },
            );
            logger.info('LT tunnel info', ltInfo);
        } catch (err) {
            Object.assign(err, { stdoutResult, stderrResult });
            logger.error('Failed to start LT tunnel', { err });
            throw err;
        }
    }

    public static async disconnectTunnel(runnerOptions: Partial<TunnelOptions> | RunnerOptions) {
        if ((runnerOptions as RunnerOptions).externalLambdatestTunnelId || !LambdatestService.tunnel) {
            return undefined;
        }
        return new Promise<void>((resolve, reject) => {
            LambdatestService.tunnel!.on('close', (code) => {
                if (code) {
                    reject(new Error(`tunnel process exited with code ${code}`));
                }
                resolve();
            });
            LambdatestService.tunnel!.kill();
        });
    }

    getCapabilities(
        runnerOptions: RunnerOptions,
        browser: string,
        executionId: string,
        testResultId: string,
        testName: string,
        testRunConfig?: any,
        overrideConfiguration?: {
            os?: (typeof OSS)[number]['osName'];
            resolution?: { width?: number; height?: number };
        },
        mode?: RunnerMode,
    ) {
        if (!this.isActive) {
            return {} as never;
        }

        const defaultBrowserCaps =
            LambdatestService.lambdatestConfig!.CAPABILITIES[browser as keyof LambdaTestConfig['CAPABILITIES']] || {};

        const tunnelCaps = {
            ...(LambdatestService.tunnelName && { tunnel: true, tunnelName: LambdatestService.tunnelName }),
        };
        let loadExtension: string[] = [];
        const { canary, ext, extensionPath, installCustomExtension } = runnerOptions;
        const config: { platform: string; resolution: string; version?: string } = {
            platform:
                getOsIfAllowed(testResultId, overrideConfiguration?.os) ||
                getOsIfAllowed(testResultId, testRunConfig?.lt?.platform) ||
                (defaultBrowserCaps as any).platform ||
                LambdatestService.lambdatestConfig!.PLATFORM,
            resolution: LambdatestService.lambdatestConfig!.RESOLUTION ?? MAX_RESOLUTION,
            ...(browser !== 'safari' && { version: defaultBrowserCaps.version }),
        };

        if (mode !== CLI_MODE.APPIUM && mode !== CLI_MODE.SELENIUM && !ext) {
            const extUrls = getExtensionsUrl({ canary } as any);
            if (!extensionPath && extUrls[browser as keyof typeof extUrls]) {
                loadExtension = [...loadExtension, extUrls[browser as keyof typeof extUrls]];
            }
            // Only pass URLs to loadExtension - local files are handled via base64 in testimDesiredCapabilitiesBuilder
            if (extensionPath && utils.isURL(extensionPath)) {
                loadExtension = [...loadExtension, extensionPath];
            }
            // Don't warn if extensionPath is a local file - it will be handled via base64
            if (!loadExtension.length && !extensionPath) {
                logger.warn('No extension was loaded in LambdaTestService', { extUrls, extensionPath });
            }
        }

        if (
            featureFlagsService.flags.enableFixForCustomExtensionForLT.isEnabled() &&
            installCustomExtension &&
            utils.isURL(installCustomExtension)
        ) {
            loadExtension = [...loadExtension, installCustomExtension];
        }
        const region = getRegionFromProjectId(runnerOptions.project || '');
        const LAMBDATEST_REGION_MAP = {
            euc1: 'eu',
        } as const;
        const ltRegion = LAMBDATEST_REGION_MAP[region as keyof typeof LAMBDATEST_REGION_MAP];
        const regionCaps = ltRegion ? { region: ltRegion } : {};

        const smartUICaps = this.smartUIIntegrationData?.projectToken ? {
            smartUI: {
                project: this.smartUIIntegrationData.projectToken,
                build: executionId,
            }
        } : {};

        const ltOptions = { 'LT:Options': {
            build: executionId,
            name: `${testResultId} - ${testName}`,
            ...regionCaps,
            // eslint-disable-next-line camelcase
            selenium_version: LambdatestService.lambdatestConfig!.SELENIUM_VERSION,
            timezone: LambdatestService.lambdatestConfig!.TIMEZONE,
            ...defaultBrowserCaps,
            ...config,
            loadExtension,
            ...tunnelCaps,
            ...smartUICaps,
            console: true,
            queueTimeout: 300, // time a session spends in the LT queue, in seconds (apparently 300 is the minimum)
            // visual: true, // [NOTE]: activate LT screenshots feature (can slow test).
            network: featureFlagsService.flags.LTNetworkCapabilities.isEnabled(), // [NOTE]: activate LT capture network logs feature (can cause network issues).
            // fixedIP: '10.80.34.143', // [NOTE]: this is for debug purposes with LT team.
            ntlm: featureFlagsService.flags.enableLambdaTestTunnelNTLM.isEnabled(),
            skipBinaryUpgrade: featureFlagsService.flags.enableLambdaTestTunnelSkipUpgrade.isEnabled(),
        } };

        return ltOptions;
    }
}

const allowedLambdatestOss = new Set([
    'Linux',
    'Windows 10',
    'Windows 11',
    'MacOS Tahoe',
    'MacOS Sequoia',
    'macOS Sonoma',
    'macOS Ventura',
    'macOS Monterey',
    'macOS Catalina',
    'macOS Big Sur',
    'macOS Mojave',
    'macOS High Sierra',
    'macOS Sierra',
    'OS X El Capitan',
    'OS X Yosemite',
    'OS X Mountain Lion',
]);

function getOsIfAllowed(testResultId: string, osRequested: string | undefined) {
    if (!osRequested) {
        return undefined;
    }

    if (!allowedLambdatestOss.has(osRequested)) {
        logger.warn(`OS ${osRequested} is not supported by LT`, { testResultId });
        return undefined;
    }

    return osRequested;
}
